// Simple auth storage and helpers (SimpleJWT)
// Persist auth to localStorage and mirror into in-memory variables for fast access
import { clearHelpStorage } from './helpStorage'
import i18n from '../i18n'
import { API_BASE_URL, endpoints } from './endpoints'

export type User = {
  id: number
  name: string
  username: string
  first_name: string
  last_name: string
  cellphone: string | null
  email: string
  allow_delivery_notifications?: boolean
  allow_email_notifications: boolean
  allow_push_notifications: boolean
  allow_sms_notifications: boolean
  allow_whatsapp_notifications: boolean
  preferred_language: string
  is_superuser: boolean
  is_active: boolean
  last_login: string | null
}

type PersistedAuth = {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
}

type LoginResponse = {
  access?: string | null
  refresh?: string | null
  user?: User | null
}

type RegisterResponse = {
  access?: string | null
  refresh?: string | null
  user?: { id?: number; name?: string; email?: string; preferred_language?: string } | null
}

type InvitationResponse = {
  access?: string | null
  refresh?: string | null
  account: { id: string; name: string }
}

const STORAGE_KEY = 'auth'
const TRIAL_NOTICE_KEY = 'show-trial-notice'

let accessToken: string | null = null
let refreshToken: string | null = null
let currentUser: User | null = null
let refreshPromise: Promise<string | null> | null = null

// Simple pub/sub so UI can react to auth changes without a global state lib
const listeners = new Set<() => void>()
export function subscribeAuth(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
function notify() {
  for (const l of listeners) l()
}

function saveStorage() {
  const data: PersistedAuth = { accessToken, refreshToken, user: currentUser }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // ignore quota errors
  }
}

function loadStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const data = JSON.parse(raw) as PersistedAuth
    accessToken = data.accessToken ?? null
    refreshToken = data.refreshToken ?? null
    currentUser = data.user ?? null
  } catch {
    // ignore
  }
}

// Initialize from storage on module load
loadStorage()

// Apply preferred language from stored user; default to pt-BR
try {
  const pref = ((u: unknown) =>
    (u && typeof u === 'object' && 'preferred_language' in u)
      ? (u as { preferred_language?: string }).preferred_language
      : undefined
  )(currentUser) ?? 'pt-BR'
  const lang = mapPreferredLanguage(pref)
  if (i18n.language !== lang) void i18n.changeLanguage(lang)
} catch {
  // Ignore invalid persisted language preferences.
}

// Normalize backend preferred_language codes to i18n codes
export function mapPreferredLanguage(code: string): string {
  // Accept 'pt', 'pt-BR', 'en', 'es' and map to configured i18n keys
  switch ((code || '').toLowerCase()) {
    case 'pt':
    case 'pt-br':
      return 'pt-BR'
    case 'en':
      return 'en'
    case 'es':
      return 'es'
    default:
      return 'pt-BR'
  }
}

export function setAccessToken(token: string | null) {
  accessToken = token
  saveStorage()
}

export function setRefreshToken(token: string | null) {
  refreshToken = token
  saveStorage()
}

export function setUser(user: User | null) {
  currentUser = user
  saveStorage()
  notify()
}

export function getUser() {
  return currentUser
}

export function getAccessToken() {
  return accessToken
}

export function getRefreshToken() {
  return refreshToken
}

export function isAuthenticated() {
  return !!accessToken
}

function scheduleTrialNotice() {
  try {
    sessionStorage.setItem(TRIAL_NOTICE_KEY, '1')
  } catch {
    // Ignore unavailable session storage.
  }
}

export function consumeTrialNotice() {
  try {
    if (sessionStorage.getItem(TRIAL_NOTICE_KEY) !== '1') return false
    sessionStorage.removeItem(TRIAL_NOTICE_KEY)
    return true
  } catch {
    return false
  }
}

function loginErrorMessage(rawMessage: string | null) {
  const normalized = rawMessage?.trim().toLowerCase().replace(/[.!]+$/, '')
  if (normalized === 'invalid platform credentials'
    || normalized === 'invalid credentials'
    || normalized === 'no active account found with the given credentials') {
    return i18n.t('errors:invalid_credentials')
  }
  return rawMessage?.trim() || i18n.t('errors:invalid_credentials')
}

export async function login(username: string, password: string) {
  // Django SimpleJWT endpoints
  const res = await fetch(`${API_BASE_URL}${endpoints.auth.token()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language || 'pt-BR',
    },
    body: JSON.stringify({ username, password })
  })
  if (!res.ok) {
    let rawMessage: string | null = null
    try {
      const payload = await res.json() as { detail?: unknown; message?: unknown }
      if (typeof payload.detail === 'string') rawMessage = payload.detail
      else if (typeof payload.message === 'string') rawMessage = payload.message
    } catch {
      // Some authentication failures have no JSON response body.
    }
    throw new Error(loginErrorMessage(rawMessage))
  }
  const data = await res.json() as unknown as LoginResponse
  // Expected shape per backend: { access: '...', refresh: '...', user: {...} }
  setAccessToken(data.access ?? null)
  setRefreshToken(data.refresh ?? null)
  const user = data.user ?? null
  setUser(user)
  scheduleTrialNotice()
  // Apply user's preferred language immediately if available
  if (user?.preferred_language) {
    const lang = mapPreferredLanguage(user.preferred_language)
    void i18n.changeLanguage(lang)
  }
}

type RegisterPayload = {
  name: string
  email: string
  password: string
  account_name: string
  cnpj: string
  plan_code: string
  accepted_terms: boolean
  terms_version: number
  privacy_policy_version: number
}

function apiErrorMessage(data: unknown) {
  if (!data || typeof data !== 'object') return null
  const values = Object.values(data as Record<string, unknown>)
  for (const value of values) {
    if (typeof value === 'string' && value) return value
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  }
  return null
}

export async function registerAccount(payload: RegisterPayload) {
  const response = await fetch(`${API_BASE_URL}${endpoints.auth.register()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = await response.json().catch(() => null) as unknown as RegisterResponse
  if (!response.ok) {
    throw new Error(apiErrorMessage(data) ?? 'Não foi possível criar a conta. Confira os dados e tente novamente.')
  }

  setAccessToken(data.access ?? null)
  setRefreshToken(data.refresh ?? null)
  const name = String(data.user?.name ?? payload.name)
  const [firstName = '', ...lastNames] = name.trim().split(/\s+/)
  setUser({
    id: data.user?.id ?? 0,
    name,
    username: data.user?.email ?? payload.email,
    first_name: firstName,
    last_name: lastNames.join(' '),
    cellphone: null,
    email: data.user?.email ?? payload.email,
    allow_email_notifications: true,
    allow_push_notifications: true,
    allow_sms_notifications: false,
    allow_whatsapp_notifications: false,
    preferred_language: 'pt-br',
    is_superuser: false,
    is_active: true,
    last_login: null,
  })
  scheduleTrialNotice()
  return data
}

export async function acceptInvitation(token: string, payload: { name: string; password: string }) {
  const response = await fetch(`${API_BASE_URL}${endpoints.auth.invitation()}?token=${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    body: JSON.stringify(payload),
  })
  const data = await response.json().catch(() => null) as unknown as InvitationResponse
  if (!response.ok) {
    throw new Error(apiErrorMessage(data) ?? 'Não foi possível ativar o acesso. Confira o convite e tente novamente.')
  }

  setAccessToken(data.access ?? null)
  setRefreshToken(data.refresh ?? null)
  setUser(null)
  return data
}

export function logout() {
  clearHelpStorage()
  setAccessToken(null)
  setRefreshToken(null)
  setUser(null)
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore unavailable local storage.
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshToken) return null
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}${endpoints.auth.refresh()}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken })
        })
        if (!res.ok) return null
        const data = await res.json() as unknown as { access?: string }
        const token = data.access
        setAccessToken(token ?? null)
        return token ?? null
      } catch {
        return null
      } finally {
        // Allow subsequent refresh attempts
        setTimeout(() => { refreshPromise = null }, 0)
      }
    })()
  }
  return refreshPromise
}
